import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronIcon, EditIcon, EmptyState, ErrorState, LoadingState, SearchIcon, useToast } from "@signet/ui";
import { useMe } from "@signet/web-kit";
import { fetchSetup, keys, saveSetup, type CreatorSetup } from "./api.ts";
import { CreatorChip, IgsPage, useIgsCreatorPhotos } from "./shared.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Setup (the boss, 2026-10-22): one row per creator with their duplication
   link, unique link, how many accounts they have on phones now and how many
   they need. A saved link shows as its Drive folder's name, linked, with a
   pencil that opens the box (Jay, 2026-10-22); an empty one is the box. The
   number is typed straight in. Both save when the box is left or Enter is
   pressed. A row is red while the
   creator has fewer accounts than they need. Creators with nothing set yet
   sit in one dropdown under the rest (Jay, 2026-10-22, as on Main Accounts).
   Clicking a header sorts by it and clicking again flips it; the page opens
   on Now, most accounts first (Jay, 2026-10-26). The counts sit beside the
   creator, links after them, and a search in the Creators header narrows
   the rows (Jay, 2026-10-27).
   ────────────────────────────────────────────────────────────────────────── */

type Change = { duplicationLink?: string | null; uniqueLink?: string | null; required?: number | null };
type SortKey = "creator" | "duplication" | "unique" | "current" | "required";
type Sort = { key: SortKey; desc: boolean };

const COLUMNS: { key: SortKey; label: string; num?: true }[] = [
  { key: "creator", label: "Creator" },
  { key: "current", label: "Now", num: true },
  { key: "required", label: "Required", num: true },
  { key: "duplication", label: "Duplication link" },
  { key: "unique", label: "Unique link" },
];
const sortValue: Record<SortKey, (r: CreatorSetup) => string | number | null> = {
  creator: (r) => r.creator,
  // A link sorts by the folder name it shows as.
  duplication: (r) => r.duplicationName ?? r.duplicationLink,
  unique: (r) => r.uniqueName ?? r.uniqueLink,
  current: (r) => r.current,
  required: (r) => r.required,
};
/** Empty cells stay at the bottom whichever way a column is sorted; ties go A to Z by creator. */
function sortRows(rows: CreatorSetup[], { key, desc }: Sort) {
  return [...rows].sort((a, b) => {
    const x = sortValue[key](a), y = sortValue[key](b);
    if (x === null || y === null) return x === y ? a.creator.localeCompare(b.creator) : x === null ? 1 : -1;
    const order = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), undefined, { sensitivity: "base" });
    return (desc ? -order : order) || a.creator.localeCompare(b.creator);
  });
}

const messageOf = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong.");
export const short = (r: CreatorSetup) => r.required !== null && r.current < r.required;
const blank = (r: CreatorSetup) => !r.duplicationLink && !r.uniqueLink && r.required === null;

export function SetupPage() {
  const { can } = useMe();
  // Admins only, unless someone is granted Setup tab edit (Jay, 2026-10-22).
  const canEdit = can("igsuicide.setup:edit");
  const photos = useIgsCreatorPhotos();
  const client = useQueryClient();
  const toast = useToast();
  const list = useQuery({ queryKey: keys.setup, queryFn: fetchSetup, refetchInterval: 60_000 });
  const save = useMutation({
    mutationFn: ({ creator, change }: { creator: string; change: Change }) => saveSetup(creator, change),
    onMutate: async ({ creator, change }) => {
      await client.cancelQueries({ queryKey: keys.setup });
      const before = client.getQueryData<{ creators: CreatorSetup[] }>(keys.setup);
      // A changed link's folder name is unknown until the server has looked it up.
      const names = { ...(change.duplicationLink !== undefined ? { duplicationName: null } : {}), ...(change.uniqueLink !== undefined ? { uniqueName: null } : {}) };
      client.setQueryData<{ creators: CreatorSetup[] }>(keys.setup, (d) => d && { creators: d.creators.map((r) => (r.creator === creator ? { ...r, ...change, ...names } : r)) });
      return { before };
    },
    onError: (error, { creator }, context) => {
      if (context?.before) client.setQueryData(keys.setup, context.before);
      toast.error(`Couldn't save ${creator}`, messageOf(error));
    },
    // The server may tidy a link (https:// added), so read back what it kept.
    onSettled: () => client.invalidateQueries({ queryKey: keys.setup }),
  });
  const rows = list.data?.creators ?? [];
  const missing = rows.filter(short).length;
  const [showBlank, setShowBlank] = useState(false);
  const [sort, setSort] = useState<Sort>({ key: "current", desc: true });
  const [query, setQuery] = useState("");
  const wanted = query.trim().toUpperCase();
  // Numbers start high to low, words A to Z.
  const sortBy = (key: SortKey, num?: true) => setSort((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: !!num }));
  // Which side a creator is on is settled when the page loads, so a row being filled in (or cleared)
  // doesn't jump out from under the cursor the moment its first box saves.
  const blankAtLoad = useRef<Map<string, boolean> | null>(null);
  if (!blankAtLoad.current && list.data) blankAtLoad.current = new Map(rows.map((r) => [r.creator, blank(r)]));
  const inGroup = (r: CreatorSetup) => blankAtLoad.current?.get(r.creator) ?? blank(r);
  const sorted = sortRows(wanted ? rows.filter((r) => r.creator.toUpperCase().includes(wanted)) : rows, sort);
  // A search reaches into the dropdown too, so a creator not set up yet is still found.
  const groupOpen = showBlank || !!wanted;
  const setUp = sorted.filter((r) => !inGroup(r));
  const notYet = sorted.filter(inGroup);
  const line = (r: CreatorSetup) => (
    <div key={r.creator} className={`table-row${short(r) ? " short" : ""}`} role="row">
      <span role="cell" className="igs-cs-who"><CreatorChip name={r.creator} photos={photos} size={26} /></span>
      <span role="cell" className="num tabular igs-cs-now">{r.current}</span>
      <NumberCell value={r.required} label={`${r.creator} required accounts`} canEdit={canEdit} onSave={(v) => save.mutateAsync({ creator: r.creator, change: { required: v } })} />
      <LinkCell value={r.duplicationLink} name={r.duplicationName} label={`${r.creator} duplication link`} canEdit={canEdit} onSave={(v) => save.mutateAsync({ creator: r.creator, change: { duplicationLink: v } })} />
      <LinkCell value={r.uniqueLink} name={r.uniqueName} label={`${r.creator} unique link`} canEdit={canEdit} onSave={(v) => save.mutateAsync({ creator: r.creator, change: { uniqueLink: v } })} />
    </div>
  );

  return (
    <IgsPage intro="Each creator's duplication link, unique link and how many accounts they need. Now counts their live accounts on phones in the Accounts tab. A row is red while a creator has fewer than they need.">
      {list.isLoading ? (
        <LoadingState />
      ) : list.isError ? (
        <ErrorState title="Couldn't load the creators" onRetry={() => list.refetch()} />
      ) : !rows.length ? (
        <EmptyState title="No creators yet" body="Creators added in the Creators tab show here." />
      ) : (
        <section className="panel section-gap">
          <div className="panel-head">
            <div>
              <h2>Creators</h2>
              <p>
                {missing
                  ? `${missing} ${missing === 1 ? "creator is" : "creators are"} short of accounts.`
                  : rows.some((r) => r.required !== null)
                    ? "Every creator with a required number has the accounts they need."
                    : "Type how many accounts each creator needs under Required."}
              </p>
            </div>
            <label className="igs-search">
              <SearchIcon size={13} />
              <input
                type="search"
                value={query}
                placeholder="Search creators"
                aria-label="Search creators"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => (e.key === "Escape" ? setQuery("") : undefined)}
              />
            </label>
          </div>
          <div className="panel-body flush">
            <div className="table-scroll">
              <div className="table igs-cs" role="table" aria-label="Creator setup">
                <div className="table-row head" role="row">
                  {COLUMNS.map(({ key, label, num }) => {
                    const on = sort.key === key;
                    return (
                      <span key={key} role="columnheader" className={num ? "num" : undefined} aria-sort={on ? (sort.desc ? "descending" : "ascending") : "none"}>
                        <button type="button" className={on ? "sorted" : undefined} onClick={() => sortBy(key, num)}>
                          {label}
                          {on ? <ChevronIcon size={12} direction={sort.desc ? "down" : "up"} /> : null}
                        </button>
                      </span>
                    );
                  })}
                </div>
                {setUp.map(line)}
                {notYet.length ? (
                  <div className={`igs-cs-group${groupOpen ? " open" : ""}`} role="rowgroup">
                    <button type="button" className="igs-cs-group-line" aria-expanded={groupOpen} onClick={() => setShowBlank(!groupOpen)}>
                      <ChevronIcon size={14} direction={groupOpen ? "down" : "right"} />
                      Not set up yet
                      <span className="igs-cs-group-count">{notYet.length}</span>
                    </button>
                    {groupOpen ? <div className="igs-cs-group-list">{notYet.map(line)}</div> : null}
                  </div>
                ) : null}
                {wanted && !sorted.length ? <p className="igs-empty">No creator matches "{query.trim()}".</p> : null}
              </div>
            </div>
          </div>
        </section>
      )}
    </IgsPage>
  );
}

/** A short green edge on a box that just saved, so a save that happens on leaving the box is seen. */
function useSaved() {
  const [saved, setSaved] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  return {
    saved,
    flash: () => {
      setSaved(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setSaved(false), 1400);
    },
  };
}

/** A link Drive couldn't name reads as its address, without the scheme. */
const shortLink = (url: string) => url.replace(/^https?:\/\//i, "").replace(/\/$/, "");

/**
 * A saved link is its folder's name, linked, and a pencil. The pencil, or an
 * empty link, is the box: saved on leaving it or Enter, Escape puts it back.
 */
function LinkCell({ value, name, label, canEdit, onSave }: { value: string | null; name: string | null; label: string; canEdit: boolean; onSave: (value: string | null) => Promise<unknown> }) {
  const [draft, setDraft] = useState(value ?? "");
  const [editing, setEditing] = useState(false);
  const { saved, flash } = useSaved();
  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);
  const shown = value ? (
    <a className="igs-cs-name" href={value} target="_blank" rel="noreferrer noopener" title={value}>
      {name ?? shortLink(value)}
    </a>
  ) : null;
  if (!canEdit) return <span role="cell" className="igs-cs-link">{shown ?? <span className="faint">-</span>}</span>;
  if (value && !editing) {
    return (
      <span role="cell" className={`igs-cs-link${saved ? " saved" : ""}`}>
        {shown}
        <button type="button" className="icon-button ghost igs-cs-edit" onClick={() => setEditing(true)} aria-label={`Edit ${label}`} title="Edit the link">
          <EditIcon size={14} />
        </button>
      </span>
    );
  }
  const commit = () => {
    const next = draft.trim() || null;
    setEditing(false);
    if (next === (value ?? null)) return;
    onSave(next).then(flash, () => setDraft(value ?? ""));
  };
  return (
    <span role="cell" className="igs-cs-link">
      <input
        className={saved ? "saved" : undefined}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            setDraft(value ?? "");
            setEditing(false);
          }
        }}
        // Opened with the pencil: straight into the box, ready to change.
        autoFocus={editing}
        onFocus={(e) => editing && e.currentTarget.select()}
        placeholder="Paste a link"
        aria-label={label}
        spellCheck={false}
        autoComplete="off"
      />
    </span>
  );
}

function NumberCell({ value, label, canEdit, onSave }: { value: number | null; label: string; canEdit: boolean; onSave: (value: number | null) => Promise<unknown> }) {
  const [draft, setDraft] = useState(value === null ? "" : String(value));
  const { saved, flash } = useSaved();
  useEffect(() => setDraft(value === null ? "" : String(value)), [value]);
  if (!canEdit) return <span role="cell" className="num tabular">{value ?? <span className="faint">-</span>}</span>;
  const commit = () => {
    const next = draft === "" ? null : Number(draft);
    if (next === value) return;
    onSave(next).then(flash, () => setDraft(value === null ? "" : String(value)));
  };
  return (
    <span role="cell" className="num igs-cs-required">
      <input
        className={`tabular${saved ? " saved" : ""}`}
        value={draft}
        // Numbers only (the boss): anything else typed simply doesn't go in.
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, "").slice(0, 5))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") setDraft(value === null ? "" : String(value));
        }}
        inputMode="numeric"
        pattern="[0-9]*"
        placeholder="-"
        aria-label={label}
        autoComplete="off"
      />
    </span>
  );
}
